package com.writerslab.office

import io.ktor.client.*
import io.ktor.client.engine.js.*
import io.ktor.client.request.forms.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.serialization.json.*
import org.w3c.dom.url.URLSearchParams

object Auth {
    private const val REQUIRED_ROLE = "wl-office"

    private val clientId: String
        get() = (window.asDynamic()._officeClientId as? String)?.takeIf { it.isNotBlank() }
            ?: "writerslab-office"
    private const val KEY_ACCESS = "office_access_token"
    private const val KEY_ID = "office_id_token"
    private const val KEY_REFRESH = "office_refresh_token"

    private val json = Json { ignoreUnknownKeys = true }

    val keycloakBase: String
        get() = (window.asDynamic()._officeKeycloakBase as? String)
            ?: "http://localhost:8180/realms/writerslab"

    val apiBase: String
        get() = (window.asDynamic()._officeApiBase as? String) ?: ""

    private val redirectUri: String
        get() = window.location.origin + "/"

    private val tokenClient = HttpClient(Js) { expectSuccess = false }

    fun getAccessToken(): String? = localStorage.getItem(KEY_ACCESS)
    private fun getIdToken(): String? = localStorage.getItem(KEY_ID)
    private fun getRefreshToken(): String? = localStorage.getItem(KEY_REFRESH)

    private fun saveTokens(access: String, id: String?, refresh: String?) {
        localStorage.setItem(KEY_ACCESS, access)
        if (id != null) localStorage.setItem(KEY_ID, id) else localStorage.removeItem(KEY_ID)
        if (refresh != null) localStorage.setItem(KEY_REFRESH, refresh) else localStorage.removeItem(KEY_REFRESH)
    }

    fun clearTokens() {
        localStorage.removeItem(KEY_ACCESS)
        localStorage.removeItem(KEY_ID)
        localStorage.removeItem(KEY_REFRESH)
    }

    val isLoggedIn: Boolean get() = getAccessToken() != null

    fun hasManagerRole(): Boolean {
        val token = getAccessToken() ?: return false
        return extractRoles(token).contains(REQUIRED_ROLE)
    }

    fun login() {
        val url = "$keycloakBase/protocol/openid-connect/auth" +
                "?client_id=$clientId" +
                "&response_type=code" +
                "&redirect_uri=${encodeURIComponent(redirectUri)}" +
                "&scope=openid%20email%20profile%20offline_access"
        window.location.href = url
    }

    fun logout() {
        val idToken = getIdToken()
        clearTokens()
        val url = "$keycloakBase/protocol/openid-connect/logout" +
                "?id_token_hint=${idToken ?: ""}" +
                "&post_logout_redirect_uri=${encodeURIComponent(redirectUri)}"
        window.location.href = url
    }

    suspend fun handleCallback(): Boolean {
        val code = URLSearchParams(window.location.search).get("code") ?: return false
        val payload = tokenClient.post("$keycloakBase/protocol/openid-connect/token") {
            setBody(FormDataContent(Parameters.build {
                append("grant_type", "authorization_code")
                append("client_id", clientId)
                append("code", code)
                append("redirect_uri", redirectUri)
            }))
        }.bodyAsText()
        val obj = try { json.parseToJsonElement(payload).jsonObject } catch (_: Throwable) { return false }
        val access = obj["access_token"]?.jsonPrimitive?.contentOrNull ?: return false
        val id = obj["id_token"]?.jsonPrimitive?.contentOrNull
        val refresh = obj["refresh_token"]?.jsonPrimitive?.contentOrNull
        saveTokens(access, id, refresh)
        window.history.replaceState(js("({})"), "", window.location.pathname)
        return true
    }

    suspend fun refreshAccessToken(): Boolean {
        val refresh = getRefreshToken() ?: return false
        val payload = try {
            tokenClient.post("$keycloakBase/protocol/openid-connect/token") {
                setBody(FormDataContent(Parameters.build {
                    append("grant_type", "refresh_token")
                    append("client_id", clientId)
                    append("refresh_token", refresh)
                }))
            }.bodyAsText()
        } catch (_: Throwable) { return false }
        val obj = try { json.parseToJsonElement(payload).jsonObject } catch (_: Throwable) { return false }
        val access = obj["access_token"]?.jsonPrimitive?.contentOrNull ?: run {
            if (obj["error"]?.jsonPrimitive?.contentOrNull == "invalid_grant") clearTokens()
            return false
        }
        val newRefresh = obj["refresh_token"]?.jsonPrimitive?.contentOrNull
        saveTokens(access, getIdToken(), newRefresh ?: refresh)
        return true
    }

    private fun extractRoles(token: String): List<String> {
        return try {
            val parts = token.split(".")
            if (parts.size != 3) return emptyList()
            var b64 = parts[1].replace('-', '+').replace('_', '/')
            while (b64.length % 4 != 0) b64 += "="
            val decoded = window.atob(b64)
            val obj = json.parseToJsonElement(decoded).jsonObject
            @Suppress("UNCHECKED_CAST")
            (obj["realm_access"]?.jsonObject?.get("roles") as? JsonArray)
                ?.mapNotNull { it.jsonPrimitive.contentOrNull }
                ?: emptyList()
        } catch (_: Throwable) {
            emptyList()
        }
    }
}

private fun encodeURIComponent(s: String): String =
    js("encodeURIComponent(s)") as String
